/**
 * DebugDiagnosticsPanel.tsx
 *
 * PR 2 — Collapsible debug diagnostics panel for the evaluation results page.
 *
 * Audience (design §3, owner ruling 2026-07-25):
 *   The owner explicitly requested this panel for their own debugging. The panel
 *   is visible to COACHES and ADMINS only (isCoach=true call-site gate in
 *   EvaluationResults.tsx). Athletes never see this panel at all — the rendering
 *   is gated in EvaluationResults.tsx before this component is even imported.
 *   This component itself does not re-gate by role: the call site owns that check.
 *
 * Placement (design §3.1): inserted as the last section BEFORE the Share card
 * (between section 17 "Movement Analysis" and section 18 "Coach Comments + Share").
 *
 * Contents (design §3.2):
 *   A. Per-dimension BD-7 pricing result table — only when pricing_result exists
 *   B. Goal expanded metric details (GoalResultsSection in always-expand mode)
 *   C. Form Score Breakdown (FormScoreBreakdown)
 *   D. Economy of Motion per-landmark expansion (MovementEconomySection)
 *
 * Default state: CLOSED (defaultExpanded={false}) per owner ruling.
 *
 * Translation layer: stubs only (PR 4, deferred per owner ruling 2026-07-25 §3.4 Q4).
 */

import { BeakerIcon } from '@heroicons/react/24/outline';
import { CollapsibleSection } from '@/components/ui';
import { cn } from '@/utils/cn';
import {
  dimensionLabel,
  verdictLabel,
  verdictColorClass,
  translateDimension,
} from '@/utils/diagnosticTranslations';
import GoalResultsSection from './GoalResultsSection';
import FormScoreBreakdown from './FormScoreBreakdown';
import MovementEconomySection from './MovementEconomySection';
import type { SpatialGoalResult, AxisGoalResult, TimingGoalResult, PhaseScore, RegionScore, EconomyPhaseData, EomNarrative } from '@/types/api';

// ============================================================================
// Types — local props interface
// ============================================================================

interface PricingDimEntry {
  z: number | null;
  score: number | null;
  verdict: 'scoreable' | 'severe' | '0-class';
  Kc: number | null;
  Ks: number | null;
}

interface PricingResult {
  verdict: 'scoreable' | 'severe' | '0-class';
  dims_fired: string[];
  per_dim_scores: Record<string, PricingDimEntry>;
  aggregate_score: number;
  is_zero_class: boolean;
  template_verification_status?: string;
}

export interface DebugDiagnosticsPanelProps {
  /**
   * BD-7 pricing result from metadata.pricing_result.
   * When absent, section A is omitted entirely (not shown as N/A).
   */
  pricingResult?: PricingResult | null;

  /**
   * Goal results for section B — expanded metric detail view.
   * Same shape as the main GoalResultsSection input.
   */
  spatialGoalResults?: Record<string, SpatialGoalResult | AxisGoalResult | TimingGoalResult> | null;

  /**
   * Phase scores for section C — FormScoreBreakdown.
   * Absent → FormScoreBreakdown section is omitted.
   */
  phaseScores?: PhaseScore[];

  /**
   * Legacy region scores for FormScoreBreakdown fallback.
   */
  legacyRegionScores?: RegionScore[];

  /**
   * Economy of Motion data for section D.
   * Absent or empty → EOM section is omitted.
   */
  economyOfMotion?: Record<string, EconomyPhaseData>;

  /**
   * EOM narrative for section D.
   */
  eomNarrative?: EomNarrative;

  /** Optional extra class on the outer wrapper. */
  className?: string;
}

// ============================================================================
// Sub-section A: Per-dimension pricing table
// ============================================================================

interface PricingDimTableProps {
  pricingResult: PricingResult;
}

function PricingDimTable({ pricingResult }: PricingDimTableProps) {
  const dims = Object.entries(pricingResult.per_dim_scores);
  if (dims.length === 0) return null;

  return (
    <div data-testid="pricing-dim-table">
      {/* Aggregate summary row */}
      <div className="flex items-center gap-3 mb-3">
        <span className="text-xs font-semibold text-text-secondary uppercase tracking-wide">
          Pricing Verdict:
        </span>
        <span
          className={cn(
            'text-xs font-semibold',
            verdictColorClass(pricingResult.verdict)
          )}
          data-testid="pricing-aggregate-verdict"
        >
          {verdictLabel(pricingResult.verdict)}
        </span>
        <span className="text-xs text-text-muted">
          Aggregate score: {pricingResult.aggregate_score.toFixed(1)}
        </span>
        {pricingResult.is_zero_class && (
          <span
            className="text-xs font-semibold text-danger"
            data-testid="pricing-zero-class-flag"
          >
            (0-class — score forced to 0)
          </span>
        )}
      </div>

      {/* Per-dimension table */}
      <div className="overflow-x-auto">
        <table
          className="w-full text-xs border-collapse"
          aria-label="Per-dimension pricing measurements"
        >
          <thead>
            <tr className="border-b border-border-subtle">
              <th className="text-left py-1.5 pr-4 text-text-muted font-medium">Dimension</th>
              <th className="text-right py-1.5 px-3 text-text-muted font-medium">z-score</th>
              <th className="text-right py-1.5 px-3 text-text-muted font-medium">Score</th>
              <th className="text-right py-1.5 px-3 text-text-muted font-medium">Ks</th>
              <th className="text-right py-1.5 px-3 text-text-muted font-medium">Kc</th>
              <th className="text-right py-1.5 pl-3 text-text-muted font-medium">Verdict</th>
            </tr>
          </thead>
          <tbody>
            {dims.map(([dimKey, entry]) => {
              const translation = translateDimension(dimKey, entry.z, entry.Kc, entry.Ks);
              return (
                <tr
                  key={dimKey}
                  className="border-b border-border-subtle/40 hover:bg-dark-elevated-2/30"
                  data-testid={`pricing-dim-row-${dimKey}`}
                >
                  <td className="py-2 pr-4 text-text-primary font-medium">
                    {dimensionLabel(dimKey)}
                    <span className="ml-1.5 text-text-muted font-normal">({dimKey})</span>
                  </td>
                  <td className="py-2 px-3 text-right text-text-secondary font-mono">
                    {entry.z != null ? entry.z.toFixed(2) : <span className="text-text-muted">—</span>}
                  </td>
                  <td className="py-2 px-3 text-right text-text-secondary font-mono">
                    {entry.score != null ? entry.score.toFixed(1) : <span className="text-text-muted">—</span>}
                  </td>
                  <td className="py-2 px-3 text-right text-text-muted font-mono">
                    {entry.Ks != null ? entry.Ks.toFixed(2) : <span className="opacity-40">—</span>}
                  </td>
                  <td className="py-2 px-3 text-right text-text-muted font-mono">
                    {entry.Kc != null ? entry.Kc.toFixed(2) : <span className="opacity-40">—</span>}
                  </td>
                  <td
                    className={cn('py-2 pl-3 text-right font-semibold', verdictColorClass(entry.verdict))}
                    data-testid={`pricing-dim-verdict-${dimKey}`}
                  >
                    {verdictLabel(entry.verdict)}
                    {/* FUTURE (PR 4): translation in italic below the verdict */}
                    {translation && (
                      <p className="font-normal italic text-text-muted mt-0.5">{translation}</p>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================================================================
// Main component
// ============================================================================

/**
 * DebugDiagnosticsPanel — collapsible panel shown to coaches/admins for debugging.
 *
 * The call site in EvaluationResults.tsx gates rendering on {isCoach} so this
 * component never mounts for athletes.
 */
function DebugDiagnosticsPanel({
  pricingResult,
  spatialGoalResults,
  phaseScores,
  legacyRegionScores,
  economyOfMotion,
  eomNarrative,
  className,
}: DebugDiagnosticsPanelProps) {
  const hasPricingResult =
    pricingResult != null &&
    pricingResult.per_dim_scores != null &&
    Object.keys(pricingResult.per_dim_scores).length > 0;

  const hasGoalResults =
    spatialGoalResults != null && Object.keys(spatialGoalResults).length > 0;

  const hasFormBreakdown =
    (phaseScores != null && phaseScores.length > 0) ||
    (legacyRegionScores != null && legacyRegionScores.length > 0);

  const hasEom =
    (economyOfMotion != null && Object.keys(economyOfMotion).length > 0) ||
    eomNarrative != null;

  // Count active subsections for the badge
  const sectionCount = [hasPricingResult, hasGoalResults, hasFormBreakdown, hasEom].filter(
    Boolean
  ).length;

  return (
    <CollapsibleSection
      title="Technical Diagnostics"
      icon={BeakerIcon}
      variant="default"
      defaultExpanded={false}
      badge={sectionCount > 0 ? sectionCount : undefined}
      className={className}
      data-testid="debug-diagnostics-panel"
    >
      <div className="space-y-6" data-testid="debug-diagnostics-content">

        {/* ── A. Per-dimension BD-7 pricing scores ─────────────────────────── */}
        {hasPricingResult && (
          <section aria-label="Per-dimension pricing scores">
            <h3 className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-3">
              Per-Dimension Scores (BD-7)
            </h3>
            <PricingDimTable pricingResult={pricingResult!} />
          </section>
        )}

        {/* ── B. Goal expanded metric details ──────────────────────────────── */}
        {hasGoalResults && (
          <section aria-label="Goal metric details">
            <h3 className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-3">
              Goal Metric Details
            </h3>
            {/* allowExpand=true unconditionally inside the debug panel:
                the panel is only shown to coaches/admins so full detail is appropriate. */}
            <GoalResultsSection
              spatialGoalResults={spatialGoalResults!}
              allowExpand={true}
            />
          </section>
        )}

        {/* ── C. Form Score Breakdown ───────────────────────────────────────── */}
        {hasFormBreakdown && (
          <section aria-label="Form score breakdown">
            <h3 className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-3">
              Form Score Breakdown
            </h3>
            <FormScoreBreakdown
              phaseScores={phaseScores}
              legacyRegionScores={legacyRegionScores}
            />
          </section>
        )}

        {/* ── D. Economy of Motion per-landmark expansion ───────────────────── */}
        {hasEom && (
          <section aria-label="Economy of motion detail">
            <h3 className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-3">
              Economy of Motion
            </h3>
            <MovementEconomySection
              economyOfMotion={economyOfMotion ?? {}}
              eomNarrative={eomNarrative}
            />
          </section>
        )}

        {/* Fallback: nothing to show yet */}
        {!hasPricingResult && !hasGoalResults && !hasFormBreakdown && !hasEom && (
          <p className="text-xs text-text-muted py-2">
            No diagnostic data available for this evaluation.
          </p>
        )}
      </div>
    </CollapsibleSection>
  );
}

export default DebugDiagnosticsPanel;
